export interface Badge {
  text: string;
  type: string;
}

export interface CourtCase {
  title: string;
  description: string;
  id: string;
  link?: string;
  detail?: {
    badges?: Badge[];
    // Additional case details can be added here
  };
}

export interface CourtPrecedentGroup {
  category: string;
  cases: CourtCase[];
}

export const courtPrecedents: CourtPrecedentGroup[] = [
  {
    category: 'Supreme Court – Foundational Cases',
    cases: [
      {
        title: 'Alice Corp. v. CLS Bank (2014)',
        description: 'Abstract idea + generic computer = not eligible',
        id: '573_13-298',
        link: 'https://supreme.justia.com/cases/federal/us/573/13-298/case.pdf',
        detail: {
          badges: [
            { text: '§ 101 Ineligible', type: 'ineligible' },
            { text: 'Abstract Idea', type: 'warning' },
            { text: 'Generic Computer', type: 'warning' },
            { text: 'Two-Step Framework', type: 'guidance' },
            { text: 'Abstract Idea + Computer', type: 'ineligible' },
            { text: 'Generic Implementation', type: 'warning' },
            { text: 'Alice/Mayo Test', type: 'guidance' }
          ]
        }
      },
      {
        title: 'Mayo v. Prometheus (2012)',
        description: 'Law of nature + routine steps = not eligible',
        id: '566_10-1150',
        link: 'https://supreme.justia.com/cases/federal/us/566/10-1150/case.pdf',
        detail: {
          badges: [
            { text: '§ 101 Ineligible', type: 'ineligible' },
            { text: 'Law of Nature', type: 'warning' },
            { text: 'Routine Steps', type: 'warning' },
            { text: 'Law of Nature + Conventional Steps', type: 'ineligible' },
            { text: 'Inventive Concept Required', type: 'guidance' }
          ]
        }
      },
      {
        title: 'Bilski v. Kappos (2010)',
        description: 'Hedging method = abstract idea',
        id: '561_08-964',
        link: 'https://supreme.justia.com/cases/federal/us/561/08-964/case.pdf',
        detail: {
          badges: [
            { text: '§ 101 Ineligible', type: 'ineligible' },
            { text: 'Business Method', type: 'warning' },
            { text: 'Abstract Idea', type: 'warning' },
            { text: 'Fundamental Economic Practice', type: 'ineligible' },
            { text: 'Machine-or-Transformation Test', type: 'guidance' }
          ]
        }
      },
      {
        title: 'Diamond v. Diehr (1981)',
        description: 'Industrial application of algorithm = eligible',
        id: '450_175',
        link: 'https://tile.loc.gov/storage-services/service/ll/usrep/usrep450/usrep450175/usrep450175.pdf',
        detail: {
          badges: [
            { text: '§ 101 Eligible', type: 'eligible' },
            { text: 'Practical Application', type: 'eligible' },
            { text: 'Industrial Process', type: 'innovation' },
            { text: 'Algorithm in Physical Process', type: 'eligible' },
            { text: 'Rubber Curing Process', type: 'innovation' },
            { text: 'Pre-Alice Precedent', type: 'guidance' }
          ]
        }
      },
      {
        title: 'Gottschalk v. Benson (1972)',
        description: 'Algorithm without application = not eligible',
        id: '409-63',
        link: 'https://tile.loc.gov/storage-services/service/ll/usrep/usrep409/usrep409063/usrep409063.pdf',
        detail: {
          badges: [
            { text: '§ 101 Ineligible', type: 'ineligible' },
            { text: 'Abstract Algorithm', type: 'warning' },
            { text: 'Mathematical Algorithm', type: 'warning' },
            { text: 'Pure Algorithm', type: 'ineligible' },
            { text: 'No Practical Application', type: 'warning' },
            { text: 'BCD Conversion', type: 'guidance' }
          ]
        }
      },
      {
        title: 'Parker v. Flook (1978)',
        description: 'Updating alarm limits using math formula = not eligible',
        id: '437-584',
        link: 'https://tile.loc.gov/storage-services/service/ll/usrep/usrep437/usrep437584/usrep437584.pdf',
        detail: {
          badges: [
            { text: '§ 101 Ineligible', type: 'ineligible' },
            { text: 'Mathematical Formula', type: 'warning' },
            { text: 'Conventional Application', type: 'warning' },
            { text: 'Formula + Conventional Steps', type: 'ineligible' },
            { text: 'Insignificant Post-Solution Activity', type: 'guidance' }
          ]
        }
      },
      {
        title: 'Myriad Genetics (2013)',
        description: 'Isolated DNA = ineligible; cDNA = eligible',
        id: '569_576',
        link: 'https://supreme.justia.com/cases/federal/us/569/12-398/case.pdf',
        detail: {
          badges: [
            { text: 'Natural Phenomenon', type: 'warning' },
            { text: 'Isolated DNA Ineligible', type: 'ineligible' },
            { text: 'cDNA Eligible', type: 'eligible' },
            { text: 'Product of Nature Doctrine', type: 'guidance' },
            { text: 'Human Intervention Creates Eligibility', type: 'eligible' }
          ]
        }
      }
    ]
  },
  {
    category: 'Federal Circuit – Eligible',
    cases: [
      {
        id: 'fed_cir-2015-1244',
        title: 'Enfish v. Microsoft (2016)',
        description: 'Database architecture = eligible',
        link: 'https://www.cafc.uscourts.gov/opinions-orders/15-1244.opinion.5-10-2016.1.pdf',
        detail: {
          badges: [
            { text: '§ 101 Eligible', type: 'eligible' },
            { text: 'Software', type: 'warning' },
            { text: 'Database', type: 'warning' },
            { text: 'Software/Database Innovation', type: 'innovation' },
            { text: 'Self-Referential Table', type: 'guidance' },
            { text: 'Specific Software Architecture', type: 'eligible' },
            { text: 'Improves Computer Functionality', type: 'innovation' },
            { text: 'Step 1 Prong One Win', type: 'guidance' }
          ]
        }
      },
      {
        id: 'fed_cir-2015-1080',
        title: 'McRO v. Bandai (2016)',
        description: 'Rule-based animation = eligible',
        link: 'https://www.cafc.uscourts.gov/opinions-orders/s15-1080.opinion.9-9-2016.2.pdf',
        detail: {
          badges: [
            { text: '§ 101 Eligible', type: 'eligible' },
            { text: 'Software', type: 'warning' },
            { text: 'Algorithm', type: 'warning' },
            { text: 'Rules-Based Animation', type: 'innovation' },
            { text: 'Technological Improvement', type: 'guidance' },
            { text: 'Specific Rule-Based Process', type: 'innovation' },
            { text: 'Preemption Analysis Step One', type: 'guidance' },
            { text: 'Claim Construction Centric', type: 'eligible' }
          ]
        }
      },
      {
        id: 'fed_cir-2013-1505',
        title: 'DDR Holdings v. Hotels.com (2014)',
        description: 'Internet-specific solution = eligible',
        link: 'https://www.cafc.uscourts.gov/opinions-orders/13-1505.opinion.12-3-2014.1.pdf',
        detail: {
          badges: [
            { text: '§ 101 Eligible', type: 'eligible' },
            { text: 'Software', type: 'warning' },
            { text: 'Internet Solution', type: 'innovation' },
            { text: 'Internet-Centric Problem', type: 'eligible' },
            { text: 'Rooted in Technology', type: 'innovation' },
            { text: 'Overcomes Internet Challenge', type: 'guidance' }
          ]
        }
      },
      {
        id: 'fed_cir-2016-2254',
        title: 'Visual Memory v. NVIDIA (2017)',
        description: 'Memory config = eligible',
        link: 'https://www.cafc.uscourts.gov/opinions-orders/16-2254.opinion.8-11-2017.1.pdf',
        detail: {
          badges: [
            { text: '§ 101 Eligible', type: 'eligible' },
            { text: 'Hardware', type: 'warning' },
            { text: 'Memory Architecture', type: 'innovation' },
            { text: 'Improved Computer Functionality', type: 'eligible' },
            { text: 'Enhanced Memory System', type: 'innovation' }
          ]
        }
      },
      {
        id: 'fed_cir-2016-2520',
        title: 'Finjan v. Blue Coat (2018)',
        description: 'Virus screening with profiles = eligible',
        link: 'https://www.cafc.uscourts.gov/opinions-orders/16-2520.opinion.1-8-2018.1.pdf',
        detail: {
          badges: [
            { text: '§ 101 Eligible', type: 'eligible' },
            { text: 'Software', type: 'warning' },
            { text: 'Security Innovation', type: 'innovation' },
            { text: 'Behavior-Based Virus Scan', type: 'innovation' },
            { text: 'Non-Conventional Security', type: 'eligible' }
          ]
        }
      },
      {
        id: 'fed_cir-2015-5150',
        title: 'Thales Visionix v. U.S. (2017)',
        description: 'Inertial tracking = eligible',
        link: 'https://www.cafc.uscourts.gov/opinions-orders/15-5150.opinion.3-6-2017.1.pdf',
        detail: {
          badges: [
            { text: '§ 101 Eligible', type: 'eligible' },
            { text: 'Physical System', type: 'innovation' },
            { text: 'Improved Measurement Technique', type: 'innovation' },
            { text: 'Specific Technological Solution', type: 'eligible' }
          ]
        }
      },
      {
        id: 'fed_cir-2016-2684',
        title: 'Core Wireless v. LG (2018)',
        description: 'Interface design = eligible',
        link: 'https://www.cafc.uscourts.gov/opinions-orders/16-2684.opinion.1-23-2018.1.pdf',
        detail: {
          badges: [
            { text: '§ 101 Eligible', type: 'eligible' },
            { text: 'Software', type: 'warning' },
            { text: 'UI Innovation', type: 'innovation' },
            { text: 'Improved User Interface', type: 'innovation' },
            { text: 'Limited Screen Space Solution', type: 'eligible' }
          ]
        }
      }
    ]
  },
  {
    category: 'Federal Circuit – Ineligible',
    cases: [
      {
        id: 'fed_cir-2022-2109',
        title: 'AI Visualize, Inc. v. Nuance Communications (2024)',
        description: 'Medical scan visualization on generic server/client = ineligible',
        link: 'https://www.cafc.uscourts.gov/opinions-orders/22-2109.OPINION.4-4-2024_2296276.pdf',
        detail: {
          badges: [
            { text: '§ 101 Ineligible', type: 'ineligible' },
            { text: 'Medical Imaging', type: 'warning' },
            { text: 'Visualization/Display', type: 'warning' },
            { text: 'Functional Claiming', type: 'warning' },
            { text: 'Generic Server/Client', type: 'ineligible' },
            { text: 'Precedential Decision', type: 'guidance' }
          ]
        }
      },
      {
        id: 'fed_cir-2023-2437',
        title: 'Recentive Analytics, Inc. v. Fox Corp. (2025)',
        description: 'Database query optimization = ineligible',
        link: 'https://www.cafc.uscourts.gov/opinions-orders/23-2437.OPINION.4-18-2025_2500790.pdf',
        detail: {
          badges: [
            { text: '§ 101 Ineligible', type: 'ineligible' },
            { text: 'AI/ML', type: 'warning' },
            { text: 'Software', type: 'warning' },
            { text: 'Landmark Decision', type: 'guidance' },
            { text: 'First Major AI/ML Precedent', type: 'warning' },
            { text: "Generic 'Do It With AI' Claims", type: 'ineligible' },
            { text: 'Framework for AI Patents', type: 'guidance' }
          ]
        }
      },
      {
        id: 'fed_cir-2015-1778',
        title: 'Electric Power Group v. Alstom (2016)',
        description: 'Data collection + display = abstract',
        link: 'https://www.cafc.uscourts.gov/opinions-orders/15-1778.opinion.7-28-2016.1.pdf',
        detail: {
          badges: [
            { text: '§ 101 Ineligible', type: 'ineligible' },
            { text: 'Data Gathering', type: 'warning' },
            { text: 'Information Display', type: 'warning' },
            { text: 'Abstract Data Collection', type: 'ineligible' },
            { text: 'Conventional Computer Use', type: 'warning' }
          ]
        }
      },
      {
        id: 'fed_cir-2013-1600',
        title: 'Digitech v. Electronics for Imaging (2014)',
        description: 'Data manipulation = abstract',
        link: 'https://www.cafc.uscourts.gov/opinions-orders/13-1600.opinion.7-9-2014.1.pdf',
        detail: {
          badges: [
            { text: '§ 101 Ineligible', type: 'ineligible' },
            { text: 'Abstract Data', type: 'warning' },
            { text: 'Information as Such', type: 'ineligible' },
            { text: 'No Physical Transformation', type: 'warning' }
          ]
        }
      },
      {
        id: 'fed_cir-2016-1233',
        title: 'Smart Systems v. Chicago Transit (2017)',
        description: 'Payment system = abstract',
        link: 'https://www.cafc.uscourts.gov/opinions-orders/16-1233.opinion.10-13-2017.1.pdf',
        detail: {
          badges: [
            { text: '§ 101 Ineligible', type: 'ineligible' },
            { text: 'Business Method', type: 'warning' },
            { text: 'Fundamental Economic Practice', type: 'ineligible' },
            { text: 'Generic Computer Implementation', type: 'warning' }
          ]
        }
      },
      {
        id: 'fed_cir-2018-1739',
        title: 'ChargePoint v. SemaConnect (2019)',
        description: 'EV charging + network = abstract',
        link: 'https://www.cafc.uscourts.gov/opinions-orders/18-1739.opinion.3-28-2019.pdf',
        detail: {
          badges: [
            { text: '§ 101 Ineligible', type: 'ineligible' },
            { text: 'Abstract Idea', type: 'warning' },
            { text: 'Conventional Network Use', type: 'warning' },
            { text: 'No Specific Implementation', type: 'ineligible' }
          ]
        }
      },
      {
        id: 'fed_cir-2016-2531',
        title: 'Two-Way Media v. Comcast (2017)',
        description: 'Streaming media system = abstract',
        link: 'https://www.cafc.uscourts.gov/opinions-orders/16-2531.opinion.10-27-2017.1.pdf',
        detail: {
          badges: [
            { text: '§ 101 Ineligible', type: 'ineligible' },
            { text: 'Abstract Idea', type: 'warning' },
            { text: 'Routine Data Transmission', type: 'warning' },
            { text: 'No Inventive Concept', type: 'ineligible' }
          ]
        }
      },
      {
        id: 'fed_cir-2015-1372',
        title: 'In re TLI Communications (2016)',
        description: 'Image upload = abstract',
        link: 'https://www.cafc.uscourts.gov/opinions-orders/15-1372.opinion.5-12-2016.1.pdf',
        detail: {
          badges: [
            { text: '§ 101 Ineligible', type: 'ineligible' },
            { text: 'Abstract Idea', type: 'warning' },
            { text: 'Storing and Transmitting Data', type: 'ineligible' },
            { text: 'Generic Computer Functions', type: 'warning' }
          ]
        }
      },
      {
        id: 'fed_cir-2020-1760',
        title: 'Yu v. Apple (2021)',
        description: 'Dual-camera image processing = abstract',
        link: 'https://www.cafc.uscourts.gov/opinions-orders/20-1760.opinion.6-11-2021_1789244.pdf',
        detail: {
          badges: [
            { text: '§ 101 Ineligible', type: 'ineligible' },
            { text: 'Abstract Idea', type: 'warning' },
            { text: 'Mathematical Manipulation', type: 'ineligible' },
            { text: 'Conventional Camera Use', type: 'warning' }
          ]
        }
      }
    ]
  },
  {
    category: 'Interpretive / Procedural',
    cases: [
      {
        id: 'fed_cir-2017-1437',
        title: 'Berkheimer v. HP (2018)',
        description: '"Well-understood, routine, conventional" = factual issue',
        link: 'https://www.cafc.uscourts.gov/opinions-orders/17-1437.order.5-25-2018.1.pdf',
        detail: {
          badges: [
            { text: 'Factual Question', type: 'guidance' },
            { text: 'Step 2B Analysis', type: 'guidance' },
            { text: 'Evidence Required for Step 2B', type: 'guidance' },
            { text: 'Can\'t Decide on Pleadings Alone', type: 'guidance' }
          ]
        }
      },
      {
        id: 'fed_cir-2017-2437',
        title: 'Aatrix Software v. Green Shades (2018)',
        description: 'Reinforces factual nature of eligibility',
        link: 'https://www.cafc.uscourts.gov/opinions-orders/17-1452.opinion.2-12-2018.1.pdf',
        detail: {
          badges: [
            { text: 'Factual Question', type: 'guidance' },
            { text: 'Evidence Requirement', type: 'guidance' },
            { text: 'Reinforces Berkheimer', type: 'guidance' },
            { text: 'Factual Determinations at Step 2', type: 'guidance' }
          ]
        }
      }
    ]
  }
];
